import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Form,
  Switch,
  InputNumber,
  Select,
  Button,
  Divider,
  Typography,
  Space,
  Row,
  Col,
  Table,
  Modal,
  Input,
  message,
  Spin,
  Alert,
  Tag,
  Tooltip,
  Collapse,
} from "antd";
import {
  BellOutlined,
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  FireOutlined,
  SaveOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import apiClient from "../api/apiClient";
import { useLocation } from "react-router-dom";

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

interface UserSettings {
  [key: string]: {
    is_enabled: boolean;
    delivery_method: "in_app" | "email" | "both";
  };
}

interface NotificationRule {
  type: string;
  name: string;
  description: string;
  priority: string;
}

const NotificationSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<UserSettings>({});
  const [availableRules, setAvailableRules] = useState<NotificationRule[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null);
  const [form] = Form.useForm();
  const location = useLocation();

  const authContext = useAuth() as { user: any } | null;
  const user = authContext?.user;
  const {
    selectedCompany: selectedCompanyId,
    selectedWarehouse: selectedWarehouseId,
    setSelectedWarehouse,
  } = useSelection();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const warehouseIdFromUrl = queryParams.get("warehouseId");
    if (warehouseIdFromUrl) {
      const warehouseId = Number(warehouseIdFromUrl);
      if (warehouseId !== selectedWarehouseId) {
        setSelectedWarehouse(warehouseId);
      }
    }
  }, [location.search]);

  const fetchNotificationData = useCallback(async () => {
    if (!user?.id || !selectedWarehouseId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [rulesResponse, settingsResponse] = await Promise.all([
        apiClient.get("/api/notifications/types"),
        apiClient.get("/api/notification-settings", {
          params: {
            user_id: user.id,
            warehouse_id: selectedWarehouseId,
          },
        }),
      ]);

      const fetchedRules = rulesResponse.data.notification_types || [];
      const fetchedSettings = settingsResponse.data.settings || {};

      setAvailableRules(fetchedRules);
      setSettings(fetchedSettings);
      form.setFieldsValue(fetchedSettings);
    } catch (e) {
      const error = e as any;
      message.error("Erreur lors du chargement des données de notification.");
    } finally {
      setLoading(false);
    }
  }, [user?.id, selectedWarehouseId, form]);

  useEffect(() => {
    fetchNotificationData();
  }, [fetchNotificationData]);

  // Fetch warehouses for the selected company
  const fetchWarehouses = useCallback(async () => {
    if (!selectedCompanyId) {
      setWarehouses([]);
      return;
    }

    try {
      const response = await apiClient.get("/api/warehouses", {
        params: {
          company_id: selectedCompanyId,
        },
      });
      const warehousesData = Array.isArray(response.data)
        ? response.data
        : response.data?.warehouses || [];
      setWarehouses(warehousesData);
    } catch (error) {
      setWarehouses([]); // Ensure it's always an array even on error
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (selectedCompanyId && user?.id) {
      fetchWarehouses();
    } else {
    }
  }, [selectedCompanyId, selectedWarehouseId, user?.id, fetchWarehouses]);

  const handleSave = useCallback(async (values: any) => {
    if (!user?.id || !selectedWarehouseId) {
      message.error("Utilisateur ou établissement non sélectionné.");
      return;
    }

    setSaving(true);
    try {

      const response = await apiClient.post(
        "/api/notification-settings/user-preferences",
        {
          user_id: user.id,
          warehouse_id: selectedWarehouseId,
          settings: values,
        }
      );

      message.success("Paramètres de notification mis à jour avec succès !");
      fetchNotificationData();
    } catch (e) {
      const error = e as any;
      message.error("Une erreur est survenue lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  }, [user?.id, selectedWarehouseId, fetchNotificationData]);

  // Get priority color and icon
  const getPriorityDisplay = useCallback((priority: string) => {
    switch (priority) {
      case "critical":
        return { icon: <FireOutlined />, color: "red", label: "Critique" };
      case "high":
        return {
          icon: <ExclamationCircleOutlined />,
          color: "orange",
          label: "Élevée",
        };
      case "medium":
        return { icon: <WarningOutlined />, color: "gold", label: "Moyenne" };
      case "low":
        return {
          icon: <InfoCircleOutlined />,
          color: "green",
          label: "Faible",
        };
      default:
        return {
          icon: <InfoCircleOutlined />,
          color: "blue",
          label: "Normale",
        };
    }
  }, []);

  // Get notification type display
  const getTypeDisplay = useCallback((type: string) => {
    const typeMap: Record<
      string,
      { label: string; color: string; description: string }
    > = {
      stock_alert: {
        label: "Alerte Stock",
        color: "red",
        description: "Notifications pour les niveaux de stock bas",
      },
      payment_due: {
        label: "Échéance Paiement",
        color: "orange",
        description: "Rappels pour les paiements en retard",
      },
      order_pending: {
        label: "Commande en Attente",
        color: "blue",
        description: "Notifications pour les commandes non traitées",
      },
      production_alert: {
        label: "Alerte Production",
        color: "purple",
        description: "Notifications liées aux processus de production",
      },
      restaurant_new_order: {
        label: "Commande Restaurant",
        color: "cyan",
        description:
          "Notifications pour les commandes restaurant et menu digital",
      },
      expense_alert: {
        label: "Alerte Dépense",
        color: "gold",
        description: "Notifications pour les dépassements de budget",
      },
      expense_supplier_missing: {
        label: "Fournisseur de Dépense Manquant",
        color: "orange",
        description:
          "Rappel lorsqu’une dépense saisie sans fournisseur ne peut pas recevoir de paiement",
      },
      user_action: {
        label: "Action Utilisateur",
        color: "green",
        description: "Notifications pour les actions requises",
      },
      system_maintenance: {
        label: "Maintenance Système",
        color: "gray",
        description: "Notifications de maintenance et mises à jour",
      },
      report_ready: {
        label: "Rapport Disponible",
        color: "cyan",
        description: "Notifications quand les rapports sont prêts",
      },
      sale_completed: {
        label: "Vente Enregistrée",
        color: "green",
        description:
          "Notifications lorsqu'une vente est enregistrée (POS ou module ventes)",
      },
      purchase_received: {
        label: "Achat / Approvisionnement",
        color: "geekblue",
        description:
          "Notifications lorsqu'un achat ou approvisionnement de stock est enregistré",
      },
      stock_adjustment: {
        label: "Ajustement de Stock",
        color: "orange",
        description:
          "Notifications lors d'un ajustement manuel de stock (hausse ou baisse)",
      },
      production_completed: {
        label: "Production Terminée",
        color: "purple",
        description:
          "Notifications lorsqu'une production est effectuée et ajoutée au stock",
      },
    };

    return (
      typeMap[type] || {
        label: type,
        color: "default",
        description: "Type personnalisé",
      }
    );
  }, []);

  // Render condition form based on notification type
  const renderConditionFields = useCallback((type: string) => {
    switch (type) {
      case "stock_alert":
        return (
          <>
            <Form.Item
              name={["conditions", "threshold"]}
              label="Seuil de stock minimum"
              rules={[{ required: true, message: "Veuillez saisir le seuil" }]}
            >
              <InputNumber
                min={0}
                placeholder="Ex: 10"
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              name={["conditions", "include_warehouses"]}
              label="Établissements concernés"
            >
              <Select mode="multiple" placeholder="Tous les établissements si vide">
                {Array.isArray(warehouses) &&
                  warehouses.map((warehouse) => (
                    <Option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </>
        );

      case "payment_due":
        return (
          <>
            <Form.Item
              name={["conditions", "days_before"]}
              label="Jours avant échéance"
              rules={[
                {
                  required: true,
                  message: "Veuillez saisir le nombre de jours",
                },
              ]}
            >
              <InputNumber
                min={0}
                max={365}
                placeholder="Ex: 7"
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              name={["conditions", "amount_threshold"]}
              label="Montant minimum"
            >
              <InputNumber
                min={0}
                placeholder="Ex: 1000"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </>
        );

      case "expense_alert":
        return (
          <>
            <Form.Item
              name={["conditions", "budget_percentage"]}
              label="Pourcentage du budget (%)"
              rules={[
                { required: true, message: "Veuillez saisir le pourcentage" },
              ]}
            >
              <InputNumber
                min={0}
                max={100}
                placeholder="Ex: 80"
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              name={["conditions", "categories"]}
              label="Catégories de dépenses"
            >
              <Select
                mode="multiple"
                placeholder="Toutes les catégories si vide"
              >
                {/* Add expense categories here */}
              </Select>
            </Form.Item>
          </>
        );

      default:
        return (
          <Form.Item
            name={["conditions", "custom_value"]}
            label="Valeur personnalisée"
          >
            <Input placeholder="Valeur de condition" />
          </Form.Item>
        );
    }
  }, [warehouses]);

  const columns = useMemo(() => [
    {
      title: "Type de Notification",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: NotificationRule) => (
        <Space direction="vertical">
          <Text strong>{record.name}</Text>
          <Text type="secondary">{record.description}</Text>
        </Space>
      ),
    },
    {
      title: "Priorité",
      dataIndex: "priority",
      key: "priority",
      render: (priority: string) => {
        const colors: { [key: string]: string } = {
          high: "red",
          medium: "orange",
          low: "green",
        };
        return (
          <Tag color={colors[priority] || "default"}>
            {priority.charAt(0).toUpperCase() + priority.slice(1)}
          </Tag>
        );
      },
    },
    {
      title: "Activé",
      dataIndex: "type",
      key: "enabled",
      render: (type: string) => (
        <Form.Item name={[type, "is_enabled"]} valuePropName="checked" noStyle>
          <Switch />
        </Form.Item>
      ),
    },
    {
      title: "Méthode de Livraison",
      dataIndex: "type",
      key: "delivery_method",
      render: (type: string) => (
        <Form.Item name={[type, "delivery_method"]} noStyle>
          <Select style={{ width: 120 }}>
            <Option value="in_app">In-App</Option>
            <Option value="email">Email</Option>
            <Option value="both">Les deux</Option>
          </Select>
        </Form.Item>
      ),
    },
  ], []);

  if (!selectedWarehouseId) {
    return (
      <Card>
        <Alert
          message="Veuillez sélectionner un établissement"
          description="Vous devez sélectionner un établissement pour gérer les paramètres de notification."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 24,
              }}
            >
              <Title level={3}>
                <BellOutlined style={{ marginRight: 8 }} />
                Paramètres de Notifications
              </Title>
              <Space>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={fetchNotificationData}
                >
                  Actualiser
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => form.submit()}
                  loading={saving}
                >
                  Enregistrer les modifications
                </Button>
              </Space>
            </div>

            <Alert
              message="Gestion des notifications"
              description="Configurez les règles de notification pour votre entreprise. Vous pouvez définir des conditions spécifiques et des niveaux de priorité pour chaque type de notification."
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Collapse
              defaultActiveKey={["general"]}
              style={{ marginBottom: 24 }}
            >
              <Panel header="Paramètres Généraux" key="general">
                <Row gutter={16}>
                  <Col span={12}>
                    <Card size="small" title="Notifications par Email">
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <div>
                          <Text>
                            Recevoir les notifications critiques par email
                          </Text>
                          <Switch defaultChecked style={{ float: "right" }} />
                        </div>
                        <div>
                          <Text>Résumé quotidien des notifications</Text>
                          <Switch defaultChecked style={{ float: "right" }} />
                        </div>
                      </Space>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="Fréquence de Vérification">
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <div>
                          <Text>Vérifier toutes les</Text>
                          <Select
                            defaultValue={30}
                            style={{ width: 120, marginLeft: 8 }}
                          >
                            <Option value={5}>5 min</Option>
                            <Option value={15}>15 min</Option>
                            <Option value={30}>30 min</Option>
                            <Option value={60}>1 heure</Option>
                          </Select>
                        </div>
                      </Space>
                    </Card>
                  </Col>
                </Row>
              </Panel>
            </Collapse>

            <Spin spinning={loading}>
              <Form form={form} onFinish={handleSave}>
                <Table
                  columns={columns}
                  dataSource={availableRules}
                  rowKey="type"
                  pagination={{ pageSize: 10 }}
                  locale={{
                    emptyText: "Aucune règle de notification configurée",
                  }}
                />
              </Form>
            </Spin>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default NotificationSettings;
